import React, { useState, useEffect } from "react";
import { postForm, get, storage } from "../api/config";

const MediaUpload = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(true);
  const [uploadType, setUploadType] = useState("images"); // "images" or "videos"

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await get("public/projects");

      if (response.data && Array.isArray(response.data)) {
        setProjects(response.data);
      } else {
        setProjects([]);
      }
    } catch (err) {
      setMessage({ type: "error", text: "Failed to load projects" });
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    // Define accepted file types based on upload type
    const imageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    const videoTypes = [
      "video/mp4",
      "video/mpeg",
      "video/ogg",
      "video/webm",
      "video/quicktime",
      "video/x-msvideo",
    ];

    const acceptedTypes = uploadType === "images" ? imageTypes : videoTypes;
    const fileType = uploadType === "images" ? "image" : "video";

    // Validate file types
    const validFiles = files.filter((file) => {
      if (uploadType === "images") {
        return file.type.startsWith("image/") && imageTypes.includes(file.type);
      } else {
        return file.type.startsWith("video/") && videoTypes.includes(file.type);
      }
    });

    if (validFiles.length !== files.length) {
      const typeText =
        uploadType === "images"
          ? "JPEG, PNG, GIF, WebP"
          : "MP4, MPEG, OGG, WebM, MOV, AVI";
      setMessage({
        type: "error",
        text: `Please select only ${fileType} files (${typeText})`,
      });
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);

      // Create preview URLs
      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrls((prev) => [
            ...prev,
            {
              url: e.target.result,
              type: file.type.startsWith("image/") ? "image" : "video",
            },
          ]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedProject) {
      setMessage({ type: "error", text: "Please select a project" });
      return;
    }

    if (selectedFiles.length === 0) {
      setMessage({
        type: "error",
        text: `Please select at least one ${uploadType.slice(0, -1)} to upload`,
      });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setMessage({ type: "", text: "" });

      const formData = new FormData();

      // Append each file
      selectedFiles.forEach((file) => {
        if (uploadType === "images") {
          formData.append("images[]", file);
        } else {
          formData.append("videos[]", file);
        }
      });

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Upload media
      const endpoint =
        uploadType === "images"
          ? `projects/${selectedProject}/images`
          : `projects/${selectedProject}/images`;

      const response = await postForm(endpoint, formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setMessage({
        type: "success",
        text: `Successfully uploaded ${
          response.data?.length || 0
        } ${uploadType}!`,
      });

      // Reset form
      setSelectedFiles([]);
      setPreviewUrls([]);
      setSelectedProject("");

      // Clear success message after 5 seconds
      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
    } catch (err) {
      setMessage({
        type: "error",
        text: `Failed to upload ${uploadType}. Please try again.`,
      });
      console.error(`Error uploading ${uploadType}:`, err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatProjectName = (project) => {
    return `${project.project_name} - ${project.location} (${project.status?.status_name})`;
  };

  const getFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleUploadTypeChange = (type) => {
    setUploadType(type);
    setSelectedFiles([]);
    setPreviewUrls([]);
    setMessage({ type: "", text: "" });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Upload Project Media
          </h1>
          <p className="text-gray-600 mt-2">
            Add images and videos to your projects to showcase progress and
            completion
          </p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === "error"
                ? "bg-red-50 border border-red-200 text-red-700"
                : message.type === "success"
                ? "bg-green-50 border border-green-200 text-green-700"
                : "bg-blue-50 border border-blue-200 text-blue-700"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Upload Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleUpload}>
            {/* Media Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Media Type *
              </label>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => handleUploadTypeChange("images")}
                  className={`px-4 py-2 rounded-lg border transition-colors duration-200 ${
                    uploadType === "images"
                      ? "bg-green-100 border-green-500 text-green-700"
                      : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  📸 Images
                </button>
                <button
                  type="button"
                  onClick={() => handleUploadTypeChange("videos")}
                  className={`px-4 py-2 rounded-lg border transition-colors duration-200 ${
                    uploadType === "videos"
                      ? "bg-green-100 border-green-500 text-green-700"
                      : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  🎥 Videos
                </button>
              </div>
            </div>

            {/* Project Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Project *
              </label>
              {loading ? (
                <div className="text-gray-500">Loading projects...</div>
              ) : (
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  required
                  disabled={uploading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">Choose a project...</option>
                  {projects.map((project) => (
                    <option key={project.project_id} value={project.project_id}>
                      {formatProjectName(project)}
                    </option>
                  ))}
                </select>
              )}
              {projects.length === 0 && !loading && (
                <p className="text-sm text-gray-500 mt-1">
                  No projects available
                </p>
              )}
            </div>

            {/* File Upload Area */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select {uploadType === "images" ? "Images" : "Videos"} *
              </label>

              {/* Drop Zone */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors duration-200">
                <input
                  type="file"
                  multiple
                  accept={uploadType === "images" ? "image/*" : "video/*"}
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer block">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="mt-2">
                    <span className="text-green-600 font-medium">
                      Click to upload
                    </span>
                    <span className="text-gray-500"> or drag and drop</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {uploadType === "images"
                      ? "PNG, JPG, GIF, WebP up to 10MB each"
                      : "MP4, MPEG, OGG, WebM, MOV, AVI up to 50MB each"}
                  </p>
                </label>
              </div>

              {/* File List */}
              {selectedFiles.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Selected {uploadType === "images" ? "Images" : "Videos"} (
                    {selectedFiles.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      >
                        <div className="flex items-center space-x-3">
                          {previewUrls[index] && (
                            <div className="relative">
                              {previewUrls[index].type === "image" ? (
                                <img
                                  src={previewUrls[index].url}
                                  alt="Preview"
                                  className="w-12 h-12 object-cover rounded"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-800 rounded flex items-center justify-center">
                                  <svg
                                    className="w-6 h-6 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                </div>
                              )}
                              <div className="absolute -top-1 -right-1 bg-gray-800 text-white text-xs px-1 rounded">
                                {previewUrls[index].type === "image"
                                  ? "IMG"
                                  : "VID"}
                              </div>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {getFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          disabled={uploading}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {uploading && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={
                  uploading || selectedFiles.length === 0 || !selectedProject
                }
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    Upload {uploadType === "images" ? "Images" : "Videos"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-2">
            Upload Instructions
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Select media type (Images or Videos)</li>
            <li>• Select a project from the dropdown list</li>
            <li>• Choose one or multiple files to upload</li>
            {uploadType === "images" ? (
              <>
                <li>• Supported formats: JPEG, PNG, GIF, WebP</li>
                <li>• Maximum file size: 10MB per image</li>
              </>
            ) : (
              <>
                <li>• Supported formats: MP4, MPEG, OGG, WebM, MOV, AVI</li>
                <li>• Maximum file size: 50MB per video</li>
              </>
            )}
            <li>• You can upload multiple files at once</li>
            <li>• Files will be associated with the selected project</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MediaUpload;
